import { useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { CODING_TOPICS, type CodingTopicId } from "../../data/prep/topics";
import type { OaKind } from "../../utils/prepPlanner";
import { cleanQuestions, type Debrief, type OaWent } from "../../utils/prepEvents";
import { cn } from "../../lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "../ui/dialog";
import { MELT_DIALOG } from "../ui/melt";

// After an OA: how it went, where they got stuck, and a note. The topics they
// got stuck on move down, so the plan works on them next.

const WENT: { value: OaWent; label: string }[] = [
  { value: "well", label: "Well" },
  { value: "ok", label: "OK" },
  { value: "rough", label: "Rough" },
];

/** A build-in-stages OA: the last level finished */
const LEVELS = [0, 1, 2, 3, 4];

const CHOICE = (on: boolean) =>
  cn(
    "whitespace-nowrap rounded-[8px] border border-melt-hair bg-white px-1.5 py-1 text-[12px] font-medium text-melt-text2 transition-colors hover:border-melt-text1/25 hover:text-melt-text1",
    on && "border-melt-caramel bg-melt-peach text-melt-text1"
  );
const LABEL = "text-[12.5px] font-semibold text-melt-text1";

export default function PrepDebrief({
  company,
  kind,
  initial,
  today,
  onSave,
}: {
  company?: string;
  kind?: OaKind;
  initial?: Debrief;
  today: string;
  onSave: (debrief: Debrief) => void;
}) {
  const [went, setWent] = useState<OaWent | null>(initial?.went ?? null);
  const [topics, setTopics] = useState<Set<CodingTopicId>>(new Set(initial?.topics ?? []));
  const [levels, setLevels] = useState<number | null>(initial?.levels ?? null);
  const [note, setNote] = useState(initial?.note ?? "");
  // No share checkbox any more: a new debrief isn't shared; one shared before stays shared
  const shared = initial?.shared ?? false;
  // At least one empty line to type in
  const [questions, setQuestions] = useState<string[]>(initial?.questions?.length ? initial.questions : [""]);
  const setQuestion = (i: number, text: string) => setQuestions((list) => list.map((q, j) => (j === i ? text : q)));
  const toggle = (id: CodingTopicId) =>
    setTopics((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-3 font-grotesk text-melt-text1">
      {/* No label: the card and the dialog already ask "How did the OA go?" */}
      <div className="flex flex-col gap-1.5">
        <div role="group" aria-label="How did it go?" className="grid grid-cols-3 gap-1.5">
          {WENT.map(({ value, label }) => (
            <button key={value} type="button" className={CHOICE(went === value)} onClick={() => setWent(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {kind === "icf" && (
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>How many levels did you finish?</span>
          <div className="grid grid-cols-5 gap-1.5">
            {LEVELS.map((n) => (
              <button key={n} type="button" className={CHOICE(levels === n)} onClick={() => setLevels(n)}>
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>
          Where did you get stuck? <span className="font-normal text-melt-text3">Pick any</span>
        </span>
        <div className="flex flex-wrap gap-1">
          {CODING_TOPICS.map((topic) => {
            const on = topics.has(topic.id as CodingTopicId);
            return (
              <button
                key={topic.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(topic.id as CodingTopicId)}
                className={cn(
                  "rounded-[8px] border px-2 py-1 text-[12px] transition-colors",
                  on
                    ? "border-melt-caramel bg-melt-peach font-medium text-melt-text1"
                    : "border-melt-hair bg-white text-melt-text2 hover:border-melt-text1/25 hover:text-melt-text1"
                )}
              >
                {topic.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>
          What did they ask? <span className="font-normal text-melt-text3">In your own words</span>
        </span>
        {questions.map((question, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              value={question}
              onChange={(e) => setQuestion(i, e.target.value)}
              placeholder={i === 0 ? "e.g. Merge overlapping meeting times" : "Another question"}
              className="h-9 min-w-0 flex-1 rounded-[10px] border border-melt-hair bg-white px-3 text-[13px] text-melt-text1 outline-none placeholder:text-melt-text3 focus:border-melt-caramel"
            />
            {questions.length > 1 && (
              <button
                type="button"
                aria-label="Remove this question"
                onClick={() => setQuestions((list) => list.filter((_, j) => j !== i))}
                className="grid h-9 w-7 shrink-0 place-items-center rounded-[8px] text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
              >
                <X size={14} strokeWidth={1.75} />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => setQuestions((list) => [...list, ""])}
          className="self-start text-[12.5px] font-medium text-melt-text2 underline decoration-melt-text3/40 underline-offset-2 hover:text-melt-text1"
        >
          Add another
        </button>
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Anything to remember? (optional)"
        className="w-full resize-none rounded-[10px] border border-melt-hair bg-white px-3 py-2 text-[13px] text-melt-text1 outline-none placeholder:text-melt-text3 focus:border-melt-caramel"
      />

      <button
        type="button"
        disabled={!went}
        onClick={() =>
          went &&
          onSave({
            went,
            topics: Array.from(topics),
            ...(kind === "icf" && levels !== null ? { levels } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
            ...(cleanQuestions(questions).length ? { questions: cleanQuestions(questions) } : {}),
            shared,
            day: initial?.day ?? today,
          })
        }
        className="h-9 rounded-[10px] bg-melt-caramel text-[13.5px] font-medium text-white transition-colors hover:bg-melt-press disabled:opacity-45 disabled:hover:bg-melt-caramel"
      >
        Save
      </button>
      {topics.size > 0 && (
        <p className="-mt-1 text-[11.5px] leading-[1.45] text-melt-text3">
          Your plan works on {topics.size === 1 ? "that topic" : "those topics"} next.
        </p>
      )}
    </div>
  );
}

/** What the debrief needs: the OA, what was said before, and where to save */
export type DebriefProps = { company: string; kind?: OaKind; initial?: Debrief; today: string; onSave: (debrief: Debrief) => void };

/** The debrief in a dialog, in the middle of the screen: there's too much in it for a popover */
export function DebriefDialog({
  open,
  onOpenChange,
  trigger,
  debrief,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What opens it, if it's opened by a click on something */
  trigger?: ReactNode;
  debrief: DebriefProps;
}) {
  const { company } = debrief;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent showClose={false} className={cn(MELT_DIALOG, "w-[calc(100%-2rem)] max-w-[460px] gap-0")}>
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <DialogTitle className="text-[20px] font-semibold tracking-[-.02em] text-melt-text1">How did the {company} OA go?</DialogTitle>
            <DialogDescription className="mt-1 text-[13.5px] text-melt-text2">Three taps, and your plan learns what to work on.</DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="-mr-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
          >
            <X size={17} strokeWidth={1.75} />
          </button>
        </div>
        <div className="px-6 pb-6 pt-4">
          <PrepDebrief
            {...debrief}
            onSave={(answers) => {
              debrief.onSave(answers);
              onOpenChange(false);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
